import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { SUBSCRIPTION_PLANS } from '../../config/constants';
import { ShopierService } from '../../services/shopierService';
import { ShopierModal } from './ShopierModal';
import {
  CreditCard,
  CheckCircle2,
  Sparkles,
  Building2,
  Ship,
  ShieldCheck,
  Zap,
  Lock,
  Layers,
  AlertCircle,
} from 'lucide-react';

export const SubscriptionPlans: React.FC = () => {
  const { activeSite, sectorQuotas } = useSite();
  const [isShopierModalOpen, setIsShopierModalOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<'PLAN_CORP_ANNUAL' | 'PLAN_YACHT_ANNUAL'>('PLAN_CORP_ANNUAL');
  const [isRenewal, setIsRenewal] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const isCorpActive = sectorQuotas.CORPORATE.status === 'ACTIVE' || sectorQuotas.CORPORATE.status === 'FULL';
  const isYachtActive = sectorQuotas.YACHT_RENTAL.status === 'ACTIVE' || sectorQuotas.YACHT_RENTAL.status === 'FULL';

  const handleOpenShopier = (planId: 'PLAN_CORP_ANNUAL' | 'PLAN_YACHT_ANNUAL', renewal: boolean = false) => {
    setConflictMessage(null);

    // Single Active License Architecture Rule: Account level MAX 1 ACTIVE WEBSITE SUBSCRIPTION
    if (planId === 'PLAN_YACHT_ANNUAL' && isCorpActive && !renewal) {
      setConflictMessage(
        'Bu hesabınızda aktif bir website aboneliği bulunmaktadır (Kurumsal Web Sitesi). Aynı anda yalnızca bir website paketi kullanılabilir.'
      );
      return;
    }
    if (planId === 'PLAN_CORP_ANNUAL' && isYachtActive && !renewal) {
      setConflictMessage(
        'Bu hesabınızda aktif bir website aboneliği bulunmaktadır (Lüks Yat Kiralama). Aynı anda yalnızca bir website paketi kullanılabilir.'
      );
      return;
    }

    setSelectedPlanId(planId);
    setIsRenewal(renewal);
    setIsShopierModalOpen(true);
  };

  const corpPlan = SUBSCRIPTION_PLANS['PLAN_CORP_ANNUAL'];
  const yachtPlan = SUBSCRIPTION_PLANS['PLAN_YACHT_ANNUAL'];

  const currentPlanId = activeSite?.subscription?.planId;
  const currentSector = activeSite?.industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
  const activeSectorQuota = sectorQuotas[currentSector];

  const subEval = activeSectorQuota?.expireDate
    ? ShopierService.evaluateSubscriptionStatus(activeSectorQuota.expireDate)
    : null;

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in select-none">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 text-xs font-bold border border-amber-500/20">
          <CreditCard className="w-3.5 h-3.5" />
          <span>Şeffaf ve Sabit Yıllık Fiyatlandırma</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          Adiosoft SaaS Abonelik Paketleri
        </h1>
        <p className="text-xs sm:text-sm text-slate-500">
          İhtiyacınıza uygun sektörel paketi seçin, Shopier güvencesiyle 12 aya varan taksit imkanıyla anında yayınlayın.
        </p>
      </div>

      {/* Conflict / Single License Warning Banner */}
      {conflictMessage && (
        <div className="max-w-5xl mx-auto p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold flex items-center justify-between gap-3 shadow-xs animate-in fade-in">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <span>{conflictMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setConflictMessage(null)}
            className="text-amber-700 hover:text-amber-900 text-xs font-semibold underline shrink-0"
          >
            Kapat
          </button>
        </div>
      )}

      {/* Sector Subscription Breakdown Cards (Requirement 34) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        {/* Kurumsal Kota Özeti */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col justify-between gap-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-sm text-slate-900">Kurumsal Web Sitesi Paketi</div>
                <div className="text-xs text-slate-500">
                  Durum:{' '}
                  <span
                    className={`font-bold ${
                      sectorQuotas.CORPORATE.status === 'ACTIVE'
                        ? 'text-emerald-600'
                        : sectorQuotas.CORPORATE.status === 'EXPIRED'
                        ? 'text-rose-600'
                        : isYachtActive
                        ? 'text-slate-400'
                        : 'text-slate-500'
                    }`}
                  >
                    {sectorQuotas.CORPORATE.status === 'ACTIVE'
                      ? 'Aktif'
                      : sectorQuotas.CORPORATE.status === 'EXPIRED'
                      ? 'Abonelik Süresi Doldu'
                      : isYachtActive
                      ? 'Yat Paketi Aktif'
                      : 'Paketsiz'}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleOpenShopier('PLAN_CORP_ANNUAL', isCorpActive)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                isYachtActive && !isCorpActive
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-xs'
              }`}
            >
              <Lock className="w-3 h-3" />
              <span>
                {isCorpActive
                  ? 'Süreyi Uzat'
                  : isYachtActive
                  ? 'Mevcut Paket Bulunuyor'
                  : 'Paket Satın Al'}
              </span>
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Toplam Hak</div>
              <div className="font-extrabold text-slate-900 text-sm">{sectorQuotas.CORPORATE.total}</div>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Kullanılan</div>
              <div className="font-extrabold text-amber-600 text-sm">{sectorQuotas.CORPORATE.used}</div>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Kalan</div>
              <div className="font-extrabold text-emerald-600 text-sm">{sectorQuotas.CORPORATE.remaining}</div>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Bitiş Tarihi</div>
              <div className="font-bold text-slate-700 text-[11px] truncate">
                {sectorQuotas.CORPORATE.expireDate
                  ? new Date(sectorQuotas.CORPORATE.expireDate).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Yat Kiralama Kota Özeti */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col justify-between gap-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center font-bold">
                <Ship className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-sm text-slate-900">Lüks Yat Kiralama Paketi</div>
                <div className="text-xs text-slate-500">
                  Durum:{' '}
                  <span
                    className={`font-bold ${
                      sectorQuotas.YACHT_RENTAL.status === 'ACTIVE'
                        ? 'text-emerald-600'
                        : sectorQuotas.YACHT_RENTAL.status === 'EXPIRED'
                        ? 'text-rose-600'
                        : isCorpActive
                        ? 'text-slate-400'
                        : 'text-slate-500'
                    }`}
                  >
                    {sectorQuotas.YACHT_RENTAL.status === 'ACTIVE'
                      ? 'Aktif'
                      : sectorQuotas.YACHT_RENTAL.status === 'EXPIRED'
                      ? 'Abonelik Süresi Doldu'
                      : isCorpActive
                      ? 'Kurumsal Paket Aktif'
                      : 'Paketsiz'}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleOpenShopier('PLAN_YACHT_ANNUAL', isYachtActive)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                isCorpActive && !isYachtActive
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-xs'
              }`}
            >
              <Lock className="w-3 h-3" />
              <span>
                {isYachtActive
                  ? 'Süreyi Uzat'
                  : isCorpActive
                  ? 'Mevcut Paket Bulunuyor'
                  : 'Paket Satın Al'}
              </span>
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Toplam Hak</div>
              <div className="font-extrabold text-slate-900 text-sm">{sectorQuotas.YACHT_RENTAL.total}</div>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Kullanılan</div>
              <div className="font-extrabold text-amber-600 text-sm">{sectorQuotas.YACHT_RENTAL.used}</div>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Kalan</div>
              <div className="font-extrabold text-cyan-600 text-sm">{sectorQuotas.YACHT_RENTAL.remaining}</div>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-500">Bitiş Tarihi</div>
              <div className="font-bold text-slate-700 text-[11px] truncate">
                {sectorQuotas.YACHT_RENTAL.expireDate
                  ? new Date(sectorQuotas.YACHT_RENTAL.expireDate).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {/* 1. Kurumsal Web Sitesi Paketi */}
        <div
          className={`rounded-3xl p-8 bg-white border-2 transition shadow-lg flex flex-col justify-between ${
            currentPlanId === corpPlan.id
              ? 'border-blue-600 ring-4 ring-blue-500/10'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              {currentPlanId === corpPlan.id && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                  Aktif Planınız
                </span>
              )}
            </div>

            <div>
              <h3 className="text-xl font-extrabold text-slate-900">{corpPlan.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{corpPlan.description}</p>
            </div>

            <div className="py-4 border-y border-slate-100">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-4xl font-extrabold text-slate-900">
                  {corpPlan.priceTRY.toLocaleString('tr-TR')} TL
                </span>
                <span className="text-xs font-medium text-slate-400">/ Yıl (KDV Dahil)</span>
              </div>
              <div className="text-[11px] text-emerald-600 font-semibold mt-1">
                Aylık {Math.round(corpPlan.priceTRY / 12).toLocaleString('tr-TR')} TL'ye denk gelir
              </div>
              <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold">
                <Layers className="w-3.5 h-3.5 text-blue-600" />
                <span>Website Hakkı: {corpPlan.websiteLimit || 1} Web Sitesi</span>
              </div>
            </div>

            {/* Feature list */}
            <div className="space-y-3">
              <span className="text-xs font-bold text-slate-900 block uppercase tracking-wider">
                Paket Kapsamı:
              </span>
              {corpPlan.features.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-8">
            <button
              onClick={() => handleOpenShopier('PLAN_CORP_ANNUAL', isCorpActive)}
              className={`w-full py-3 rounded-xl font-bold text-xs shadow-lg transition flex items-center justify-center gap-2 ${
                isYachtActive && !isCorpActive
                  ? 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200'
                  : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20'
              }`}
            >
              {isYachtActive && !isCorpActive ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Mevcut Website Aboneliğiniz Bulunuyor</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  <span>{isCorpActive ? 'Aboneliği Yenile' : 'Kurumsal Pakete Geç'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* 2. Lüks Yat Kiralama Paketi */}
        <div
          className={`rounded-3xl p-8 bg-slate-900 text-white border-2 transition shadow-2xl flex flex-col justify-between relative overflow-hidden ${
            currentPlanId === yachtPlan.id
              ? 'border-cyan-400 ring-4 ring-cyan-500/20'
              : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="absolute top-0 right-0 p-4">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> En Popüler Sektörel
            </span>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
                <Ship className="w-6 h-6" />
              </div>
            </div>

            <div>
              <h3 className="text-xl font-extrabold">{yachtPlan.name}</h3>
              <p className="text-xs text-slate-400 mt-1">{yachtPlan.description}</p>
            </div>

            <div className="py-4 border-y border-slate-800">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-4xl font-extrabold text-white">
                  {yachtPlan.priceTRY.toLocaleString('tr-TR')} TL
                </span>
                <span className="text-xs font-medium text-slate-400">/ Yıl (KDV Dahil)</span>
              </div>
              <div className="text-[11px] text-cyan-400 font-semibold mt-1">
                Aylık {Math.round(yachtPlan.priceTRY / 12).toLocaleString('tr-TR')} TL'ye denk gelir
              </div>
              <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-cyan-950/60 border border-cyan-500/40 text-cyan-300 text-xs font-bold">
                <Layers className="w-3.5 h-3.5 text-cyan-400" />
                <span>Website Hakkı: {yachtPlan.websiteLimit || 1} Web Sitesi</span>
              </div>
            </div>

            {/* Feature list */}
            <div className="space-y-3">
              <span className="text-xs font-bold text-cyan-300 block uppercase tracking-wider">
                Yat Kiralama Özel Modülleri:
              </span>
              {yachtPlan.features.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-8">
            <button
              onClick={() => handleOpenShopier('PLAN_YACHT_ANNUAL', isYachtActive)}
              className={`w-full py-3 rounded-xl font-bold text-xs shadow-lg transition flex items-center justify-center gap-2 ${
                isCorpActive && !isYachtActive
                  ? 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                  : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-cyan-500/20'
              }`}
            >
              {isCorpActive && !isYachtActive ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Mevcut Website Aboneliğiniz Bulunuyor</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  <span>{isYachtActive ? 'Aboneliği Yenile' : 'Yat Kiralama Paketini Satın Al'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Shopier Modal */}
      {isShopierModalOpen && (
        <ShopierModal
          isOpen={isShopierModalOpen}
          onClose={() => setIsShopierModalOpen(false)}
          targetPlanId={selectedPlanId}
          isRenewal={isRenewal}
        />
      )}
    </div>
  );
};
